import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

let seq = 0;
const newLine = () => ({ key: ++seq, product_id: "", boxes: 1 });

function LineRow({ line, i, products, onChange, onRemove, canRemove }) {
  const p = products.find((x) => x.id === line.product_id);
  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3" data-testid={`po-line-${i}`}>
      <div className="flex gap-2">
        <Select value={line.product_id} onValueChange={(v) => onChange({ product_id: v })}>
          <SelectTrigger className="flex-1" data-testid={`po-line-product-${i}`}><SelectValue placeholder="Choose product" /></SelectTrigger>
          <SelectContent>{products.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} {x.vial_size}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" min="1" value={line.boxes} onChange={(e) => onChange({ boxes: e.target.value })} className="w-24 font-mono-data" aria-label="Boxes" data-testid={`po-line-boxes-${i}`} />
        {canRemove && <Button type="button" size="icon" variant="ghost" onClick={onRemove} data-testid={`po-line-remove-${i}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>}
      </div>
      {p && (
        <p className="font-mono-data text-xs text-muted-foreground" data-testid={`po-line-info-${i}`}>
          {Number(line.boxes) || 0} box(es) × {p.box_size} = {(Number(line.boxes) || 0) * p.box_size} vials · in stock {p.stock}
          {p.on_order_boxes > 0 && <span className="text-amber-400"> · already waiting on {p.on_order_boxes} box(es)</span>}
        </p>
      )}
    </div>
  );
}

export function NewPODialog({ open, onClose, onSaved, vendors }) {
  const [products, setProducts] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [lines, setLines] = useState([newLine()]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVendorId(vendors.length === 1 ? vendors[0].id : "");
    setLines([newLine()]);
    setNotes("");
    api.get("/admin/products").then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open, vendors]);

  const patch = (key, p) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)));
  const submit = async (e) => {
    e.preventDefault();
    if (!vendorId) return toast.error("Choose a vendor");
    const items = lines.filter((l) => l.product_id).map((l) => ({ product_id: l.product_id, boxes: Number(l.boxes) || 0 }));
    if (!items.length) return toast.error("Add at least one product");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purchase-orders", { vendor_id: vendorId, items, notes });
      toast.success(`${data.po_number} created`);
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="po-form-dialog">
        <DialogHeader><DialogTitle>New vendor order</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Vendor</Label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger data-testid="po-vendor-select"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
              <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Products and boxes</Label>
            {lines.map((l, i) => (
              <LineRow key={l.key} line={l} i={i} products={products} canRemove={lines.length > 1}
                onChange={(p) => patch(l.key, p)} onRemove={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} />
            ))}
            <Button type="button" variant="outline" className="w-full gap-2" onClick={() => setLines((ls) => [...ls, newLine()])} data-testid="po-add-line-button"><Plus className="h-4 w-4" />Add product</Button>
          </div>
          <div className="space-y-1.5"><Label>Notes for the vendor (optional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="po-notes-input" /></div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="po-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create order sheet</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
